import React, { useState, useEffect, useCallback } from 'react';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Grid,
  Tooltip,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  TablePagination,
  LinearProgress,
  Fab,
  Drawer,
  ListItemIcon,
  Menu,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  PlayArrow as PlayArrowIcon,
  GroupWork as GroupWorkIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  DragHandle as DragHandleIcon,
  Sort as SortIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Code as CodeIcon,
  TableChart as TableChartIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  Link as LinkIcon,
  Calculate as CalculateIcon,
  ListAlt as ListAltIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import sandboxesService from '../services/sandboxesService';
import projectsService from '../services/projectsService';
import { LCM, Project, Revision, Sandbox, User } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import { openFilePreviewOrDownload } from '../utils/filePreview';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Enhanced variable types and interfaces
interface Variable {
  id: string;
  name: string;
  type: 'independent' | 'dependent' | 'automatic' | 'fromList';
  values?: string;
  formula?: string;
  parent?: string;
  conditions?: Record<string, string>;
  parsedValues: any[];
  description?: string;
  fromListData?: {
    composedIndex: string;
    valueList: Array<{ composedIndex: string; value: string }>;
  };
  imported?: boolean;
}

interface ComposedIndexConfig {
  enabled: boolean;
  name: string;
  separator: string;
  selectedVariables: string[];
}

interface LCMConfig {
  variables: Variable[];
  composedIndex?: ComposedIndexConfig;
  columnOrder?: string[];
  version?: string;
  exported?: string;
  matrixHash?: string;
  yaml_template?: string;
  verification?: {
    status: boolean;
    verifiedBy?: string;
    verifiedAt?: string;
  };
}

interface Warning {
  type: string;
  title: string;
  description: string;
  variables: Array<{ name: string; issue: string }>;
  severity: 'info' | 'warning' | 'error';
}

const LCMLabPage: React.FC = () => {
  // Default templates state
  const [templates, setTemplates] = useState<Record<string, any>>({});
  const { user } = useAuth();

  // URL parameters for context
  const { projectId, revisionId, sandboxId, lcmId } = useParams<{
    projectId?: string;
    revisionId?: string;
    sandboxId?: string;
    lcmId?: string;
  }>();

  // State management
  const [project, setProject] = useState<Project | null>(null);
  const [revision, setRevision] = useState<Revision | null>(null);
  const [sandbox, setSandbox] = useState<Sandbox | null>(null);
  const [currentLcm, setCurrentLcm] = useState<LCM | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Enhanced LCM Lab state
  const [variables, setVariables] = useState<Variable[]>([]);
  const [composedIndexConfig, setComposedIndexConfig] = useState<ComposedIndexConfig>({
    enabled: false,
    name: 'CaseID',
    separator: '_',
    selectedVariables: []
  });
  const [matrixData, setMatrixData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [matrixColumns, setMatrixColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortState, setSortState] = useState<{ column: string | null; direction: 'asc' | 'desc' }>({
    column: null,
    direction: 'asc'
  });
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [matrixHash, setMatrixHash] = useState<string>('');
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Dialog states
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [composedIndexModalOpen, setComposedIndexModalOpen] = useState(false);
  const [warningsModalOpen, setWarningsModalOpen] = useState(false);
  const [loadConfigOpen, setLoadConfigOpen] = useState(false);
  const [loadCsvOpen, setLoadCsvOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [uploadTemplateOpen, setUploadTemplateOpen] = useState(false);
  const [yamlImportOpen, setYamlImportOpen] = useState(false);
  const [yamlDisplayOpen, setYamlDisplayOpen] = useState(false);
  const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
  const [dependencyRulesModalOpen, setDependencyRulesModalOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  // YML Generation state
  const [ymlGenerationTask, setYmlGenerationTask] = useState<{
    task_id: string;
    status: string;
    progress?: number;
    result_files?: any[];
  } | null>(null);
  const [ymlGenerationLoading, setYmlGenerationLoading] = useState(false);

  // Matrix Generation state
  const [matrixGenerationTask, setMatrixGenerationTask] = useState<{
    task_id: string;
    status: string;
    progress?: number;
    result_files?: any[];
  } | null>(null);
  const [matrixGenerationLoading, setMatrixGenerationLoading] = useState(false);
  const [currentMatrixTaskId, setCurrentMatrixTaskId] = useState<string | null>(null);

  // Verification state
  const [variablesVerified, setVariablesVerified] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState<{
    verifiedBy?: string;
    verifiedAt?: string;
  }>({});
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Helper function to get localStorage key for verification data
  const getVerificationStorageKey = useCallback(() => {
    if (lcmId) {
      return `lcm_verification_${projectId}_${revisionId}_${sandboxId}_${lcmId}`;
    } else if (projectId && revisionId && sandboxId) {
      return `lcm_verification_${projectId}_${revisionId}_${sandboxId}_new`;
    } else {
      return 'lcm_verification_dashboard';
    }
  }, [projectId, revisionId, sandboxId, lcmId]);

  // Load verification data from localStorage
  const loadVerificationFromStorage = useCallback(() => {
    try {
      const key = getVerificationStorageKey();
      const stored = localStorage.getItem(key);
      if (stored) {
        const data = JSON.parse(stored);
        setVariablesVerified(data.status || false);
        setVerificationDetails({
          verifiedBy: data.verifiedBy,
          verifiedAt: data.verifiedAt
        });
      }
    } catch (error) {
      console.error('Error loading verification from localStorage:', error);
    }
  }, [getVerificationStorageKey]);

  // Save verification data to localStorage
  const saveVerificationToStorage = useCallback((status: boolean, details: { verifiedBy?: string; verifiedAt?: string }) => {
    try {
      const key = getVerificationStorageKey();
      const data = {
        status,
        verifiedBy: details.verifiedBy,
        verifiedAt: details.verifiedAt
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving verification to localStorage:', error);
    }
  }, [getVerificationStorageKey]);

  // Menu states
  const [loadMenuAnchor, setLoadMenuAnchor] = useState<null | HTMLElement>(null);

  // Form states
  const [editingVariableId, setEditingVariableId] = useState<string | null>(null);
  const [currentRulesVariable, setCurrentRulesVariable] = useState<Variable | undefined>(undefined);
  const [exportName, setExportName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [yamlContent, setYamlContent] = useState('');
  const [yamlTemplate, setYamlTemplate] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [dependencyRulesData, setDependencyRulesData] = useState<Array<{ parentValue: string; childValues: string }>>([]);

  // Initial config for change detection
  const [initialConfig, setInitialConfig] = useState<LCMConfig | null>(null);

  // Function to get current config (excluding runtime fields like exported and matrixHash)
  const getCurrentConfig = useCallback((): Omit<LCMConfig, 'exported' | 'matrixHash' | 'version'> => {
    // Normalize variables to ensure consistent comparison
    const normalizedVariables = variables.map(v => ({
      id: v.id || '',
      name: v.name || '',
      type: v.type,
      values: v.values || '',
      formula: v.formula || '',
      parent: v.parent || '',
      conditions: v.conditions || {},
      parsedValues: v.parsedValues || [],
      fromListData: v.fromListData || undefined,
      description: v.description || ''
    }));

    // Normalize composed index config
    const normalizedComposedIndex = {
      enabled: composedIndexConfig.enabled || false,
      name: composedIndexConfig.name || 'CaseID',
      separator: composedIndexConfig.separator || '_',
      selectedVariables: composedIndexConfig.selectedVariables || []
    };

    return {
      variables: normalizedVariables,
      composedIndex: normalizedComposedIndex,
      columnOrder: columnOrder || [],
      yaml_template: yamlTemplate || ''
    };
  }, [variables, composedIndexConfig, columnOrder, yamlTemplate]);

  // Check for unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialConfig) {
      // For new LCMs, consider changes if there are variables defined
      return variables.length > 0;
    }

    const current = getCurrentConfig();

    // Use a more robust comparison by sorting object keys
    const currentStr = JSON.stringify(current, Object.keys(current).sort());
    const initialStr = JSON.stringify(initialConfig, Object.keys(initialConfig).sort());

    return currentStr !== initialStr;
  }, [initialConfig, getCurrentConfig, variables.length]);


  // UI states
  const [activeTab, setActiveTab] = useState(0);
  const [dependencyTreeCollapsed, setDependencyTreeCollapsed] = useState(false);


  // Load templates from lcm_config.json
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await fetch('/lcm_config.json');
        const config = await response.json();
        setTemplates({
          offshore: {
            name: "Offshore Standard",
            description: "Direction -> Hs -> Tp hierarchy with typical offshore values",
            variables: config.variables
          }
        });
      } catch (error) {
        console.error('Failed to load lcm_config.json:', error);
        // Fallback to empty templates
        setTemplates({});
      }
    };

    loadTemplates();
  }, []);

  // Load verification data from localStorage on component mount
  useEffect(() => {
    loadVerificationFromStorage();
  }, [loadVerificationFromStorage]);

  // Initialize variables and load context data
  useEffect(() => {
    const initializeData = async () => {
      // Determine mode
      const isDashboardMode = !projectId && !revisionId && !sandboxId;
      const isModifyMode = !!lcmId;

      if (isDashboardMode) {
        // Dashboard mode - start with empty variables
        setVariables([]);
        setInitialConfig(getCurrentConfig());
        setIsLoadingConfig(false);
        setIsInitialLoad(false);
      } else if (projectId && revisionId && sandboxId) {
        // Sandbox context mode
        try {
          setLoading(true);
          const [projectData, revisionData, sandboxData] = await Promise.all([
            projectsService.getProjects().then(projects => projects.find(p => p.id === parseInt(projectId))),
            projectsService.getRevisions(parseInt(projectId)).then(revisions => revisions.find(r => r.id === parseInt(revisionId))),
            sandboxesService.getSandboxes(parseInt(projectId), parseInt(revisionId)).then(sandboxes => sandboxes.find(s => s.id === parseInt(sandboxId))),
          ]);

          setProject(projectData || null);
          setRevision(revisionData || null);
          setSandbox(sandboxData || null);

          if (isModifyMode && lcmId) {
            // Load existing LCM data for editing
            setIsLoadingConfig(true);
            const lcmResponse = await sandboxesService.getLCMDetails(parseInt(projectId), parseInt(revisionId), parseInt(sandboxId), parseInt(lcmId));
            setCurrentLcm(lcmResponse.lcm);

            // Load LCM configuration
            if (lcmResponse.lcm.prepro_data) {
              const config: LCMConfig = lcmResponse.lcm.prepro_data;
              const loadedVariables = (config.variables || []).map(v => {
                const variable: Variable = {
                  id: v.id,
                  name: v.name,
                  type: v.type,
                  values: v.values || '',
                  formula: v.formula || '',
                  parent: v.parent || '',
                  conditions: v.conditions || {},
                  fromListData: v.fromListData,
                  description: v.description || '',
                  parsedValues: []
                };

                // Parse values based on type
                if (variable.type === 'independent' && variable.values) {
                  variable.parsedValues = parseValues(variable.values);
                } else if (variable.type === 'dependent') {
                  // Calculate parsed values from conditions
                  const allValues = new Set();
                  if (variable.conditions) {
                    Object.values(variable.conditions).forEach(conditionValues => {
                      parseValues(conditionValues).forEach(val => allValues.add(val));
                    });
                  }
                  variable.parsedValues = Array.from(allValues);
                } else if (variable.type === 'automatic') {
                  variable.parsedValues = [];
                } else if (variable.type === 'fromList' && variable.fromListData) {
                  // Populate parsedValues with unique values from the value list
                  const uniqueValues = Array.from(new Set(variable.fromListData!.valueList.map(item => item.value).filter(val => val && val.trim() !== '')));
                  variable.parsedValues = uniqueValues;
                }

                return variable;
              });
              setVariables(loadedVariables);
              setComposedIndexConfig(config.composedIndex || composedIndexConfig);
              setColumnOrder(config.columnOrder || []);
              setYamlTemplate(config.yaml_template || '');

              // Load verification data
              if (config.verification) {
                setVariablesVerified(config.verification.status || false);
                setVerificationDetails({
                  verifiedBy: config.verification.verifiedBy,
                  verifiedAt: config.verification.verifiedAt
                });

              } else {
                setVariablesVerified(false);
                setVerificationDetails({});
              }

              // Set initial config for change detection using the same normalization as getCurrentConfig
              const normalizedLoadedVariables = loadedVariables.map(v => ({
                id: v.id || '',
                name: v.name || '',
                type: v.type,
                values: v.values || '',
                formula: v.formula || '',
                parent: v.parent || '',
                conditions: v.conditions || {},
                parsedValues: v.parsedValues || [],
                fromListData: v.fromListData || undefined,
                description: v.description || ''
              }));

              const normalizedComposedIndexFromConfig = {
                enabled: (config.composedIndex || composedIndexConfig).enabled || false,
                name: (config.composedIndex || composedIndexConfig).name || 'CaseID',
                separator: (config.composedIndex || composedIndexConfig).separator || '_',
                selectedVariables: (config.composedIndex || composedIndexConfig).selectedVariables || []
              };

              const newInitialConfig = {
                variables: normalizedLoadedVariables,
                composedIndex: normalizedComposedIndexFromConfig,
                columnOrder: config.columnOrder || [],
                yaml_template: config.yaml_template || ''
              };
              setInitialConfig(newInitialConfig);
              setIsLoadingConfig(false);

              // Check for existing matrix
              try {
                const matrixCheck = await sandboxesService.checkLCMMatrix(parseInt(projectId), parseInt(revisionId), parseInt(sandboxId), parseInt(lcmId));
                if (matrixCheck.has_matrix && matrixCheck.task_id) {
                  // Load existing matrix data
                  const matrixDataResponse = await sandboxesService.getMatrixData(matrixCheck.task_id);
                  setMatrixData(matrixDataResponse.matrix_data);
                  setMatrixColumns(matrixDataResponse.matrix_columns);
                  setMatrixHash(matrixDataResponse.matrix_hash);
                  setFilteredData(matrixDataResponse.matrix_data);

                  // Analyze warnings
                  setTimeout(() => {
                    analyzeWarnings();
                  }, 0);

                  setSuccess(`Existing matrix loaded successfully with ${matrixDataResponse.matrix_data.length.toLocaleString()} combinations`);
                }
              } catch (matrixError) {
                console.warn('Failed to load existing matrix:', matrixError);
                // Don't fail the LCM loading if matrix loading fails
              }
            }
          } else {
            // New LCM in sandbox context - start empty
            setVariables([]);
            setInitialConfig(getCurrentConfig());
            setIsLoadingConfig(false);
          }

          setError(null);
        } catch (err: any) {
          setError(err.response?.data?.error || 'Failed to load context data');
        } finally {
          setLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    initializeData();
  }, [projectId, revisionId, sandboxId, lcmId]);

  // Auto-generate matrix when variables change
  useEffect(() => {
    if (!variableModalOpen) {
      //  generateMatrix(); // KILOCODE: Temporarily disabled to prevent FE memory overload
    }
  }, [variables, composedIndexConfig, variableModalOpen]);

  // Reset verification when LCM configuration changes (but not when loading from config)
  useEffect(() => {
    // Don't reset verification during initial load or while loading config to prevent race conditions
    if (hasUnsavedChanges() && !isLoadingConfig && !isInitialLoad && initialConfig) {
      setVariablesVerified(false);
      setVerificationDetails({});
      // Clear localStorage when verification is reset
      try {
        const key = getVerificationStorageKey();
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error clearing verification from localStorage:', error);
      }
    }
  }, [hasUnsavedChanges, getVerificationStorageKey, isLoadingConfig, isInitialLoad, initialConfig]);

  // Warn before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes to the LCM. Changes will be deleted if you leave without exporting to the database.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);





  // Utility functions
  const parseValues = (input: string): any[] => {
    try {
      if (!input || !input.trim()) return [];

      let cleaned = input.trim();

      // Handle ranges (start:step:end)
      const rangeMatches = cleaned.match(/(\d+(?:\.\d+)?):(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)/g);
      if (rangeMatches) {
        rangeMatches.forEach(rangeMatch => {
          const range = expandRange(rangeMatch);
          cleaned = cleaned.replace(rangeMatch, range.join(', '));
        });
      }

      const values = cleaned
        .split(/[,;\n]|(?:\s{2,})/)
        .map(v => v.trim())
        .filter(v => v.length > 0)
        .filter((value, index, self) => self.indexOf(value) === index);

      return values;
    } catch (error) {
      console.error('Error parsing values:', error);
      return [];
    }
  };

  // Function to detect arithmetic sequence and return range format
  const detectArithmeticSequence = (values: string[]): string => {
    const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (numericValues.length !== values.length || numericValues.length < 3) {
      return values.join(', ');
    }

    // Sort the values
    numericValues.sort((a, b) => a - b);

    // Check if it's an arithmetic sequence
    const differences = [];
    for (let i = 1; i < numericValues.length; i++) {
      differences.push(numericValues[i] - numericValues[i - 1]);
    }

    // Check if all differences are the same (within tolerance)
    const firstDiff = differences[0];
    const tolerance = 0.0001;
    const isArithmetic = differences.every(diff => Math.abs(diff - firstDiff) < tolerance);

    if (isArithmetic && firstDiff !== 0) {
      const min = numericValues[0];
      const max = numericValues[numericValues.length - 1];
      const pace = firstDiff;
      return `${min}:${pace}:${max}`;
    }

    return values.join(', ');
  };

  const expandRange = (rangeStr: string): string[] => {
    try {
      const parts = rangeStr.split(':');
      if (parts.length !== 3) return [rangeStr];

      const start = parseFloat(parts[0]);
      const step = parseFloat(parts[1]);
      const end = parseFloat(parts[2]);

      if (isNaN(start) || isNaN(step) || isNaN(end) || step <= 0) {
        return [rangeStr];
      }

      const values = [];
      for (let i = start; i <= end; i += step) {
        values.push((Math.round(i * 100) / 100).toString());
      }

      return values;
    } catch (error) {
      console.error('Error expanding range:', error);
      return [rangeStr];
    }
  };

  const evaluateFormula = (formula: string, variableValues: Record<string, any>): number => {
    try {
      // Replace variable names with their values
      let expression = formula;

      // Replace variable names with their numeric values
      for (const [varName, value] of Object.entries(variableValues)) {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
          // Use word boundaries to ensure exact variable name matching
          const regex = new RegExp(`\\b${varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
          expression = expression.replace(regex, numericValue.toString());
        }
      }

      // Replace mathematical functions with JavaScript equivalents
      expression = expression
        .replace(/\bsqrt\b/g, 'Math.sqrt')
        .replace(/\bsin\b/g, 'Math.sin')
        .replace(/\bcos\b/g, 'Math.cos')
        .replace(/\btan\b/g, 'Math.tan')
        .replace(/\blog\b/g, 'Math.log')
        .replace(/\bexp\b/g, 'Math.exp')
        .replace(/\babs\b/g, 'Math.abs')
        .replace(/\bpow\b/g, 'Math.pow')
        .replace(/\^/g, '**'); // Replace ^ with ** for exponentiation

      // Evaluate the expression
      const result = Function('"use strict"; return (' + expression + ')')();

      // Return the result rounded to 6 decimal places
      return Math.round(result * 1000000) / 1000000;
    } catch (error) {
      console.error('Error evaluating formula:', formula, error);
      return 0; // Return 0 for invalid formulas
    }
  };

  const generateComposedIndex = (row: any): string => {
    if (!composedIndexConfig.enabled || composedIndexConfig.selectedVariables.length === 0) {
      return '';
    }

    const selectedVariables = variables.filter(v =>
      composedIndexConfig.selectedVariables.includes(v.id)
    );

    // Sort variables alphabetically
    const sortedVariables = [...selectedVariables].sort((a, b) => a.name.localeCompare(b.name));

    const values = sortedVariables.map(variable => {
      const value = row[variable.name];
      return value !== undefined ? value.toString() : 'N/A';
    });

    const result = values.join(composedIndexConfig.separator);
    return result;
  };

  // Matrix generation
  const generateMatrix = useCallback(async () => {
    if (variables.length === 0) {
      setMatrixData([]);
      setFilteredData([]);
      setMatrixHash('no-data');
      setMatrixColumns([]);
      return;
    }

    try {
      setLoading(true);

      // Generate combinations respecting dependencies
      const combinations = generateHierarchicalCombinations(variables);
      setMatrixData(combinations);
      setFilteredData(combinations);

      // Set columns
      const validVariables = variables.filter(v =>
        v.type === 'automatic' || v.parsedValues.length > 0 || (v.type === 'fromList' && v.fromListData && v.fromListData.valueList.length > 0)
      );

      const columns = [
        ...(composedIndexConfig.enabled ? [composedIndexConfig.name] : []),
        ...validVariables.map(v => v.name)
      ];
      setMatrixColumns(columns);

      // Initialize column order if needed
      if (columnOrder.length === 0) {
        setColumnOrder(validVariables.map(v => v.name));
      }

      // Add composed index to each row if enabled
      if (composedIndexConfig.enabled) {
        combinations.forEach(row => {
          row[composedIndexConfig.name] = generateComposedIndex(row);
        });
      }

      // Generate matrix hash
      const hash = await generateMatrixHash(combinations);
      setMatrixHash(hash);

      // Analyze warnings
      analyzeWarnings();

      setSuccess(`Matrix generated successfully with ${combinations.length.toLocaleString()} combinations`);
    } catch (error) {
      console.error('Error generating matrix:', error);
      setError('Failed to generate matrix');
    } finally {
      setLoading(false);
    }
  }, [variables, composedIndexConfig]);

  const generateHierarchicalCombinations = (vars: Variable[]): any[] => {
    try {
      const independentVars = vars.filter(v => v.type === 'independent');
      const dependentVars = vars.filter(v => v.type === 'dependent');
      const automaticVars = vars.filter(v => v.type === 'automatic');
      const fromListVars = vars.filter(v => v.type === 'fromList');

      if (independentVars.length === 0) {
        return [];
      }

      // Start with cartesian product of independent variables
      let combinations = generateCartesianProduct(independentVars);

      // Process dependent variables level by level
      const processedVars = new Set(independentVars.map(v => v.name));
      let iterationCount = 0;
      const maxIterations = 10;

      while (processedVars.size < (vars.length - automaticVars.length) && iterationCount < maxIterations) {
        iterationCount++;
        let addedThisIteration = false;

        dependentVars.forEach(depVar => {
          if (processedVars.has(depVar.name)) return;
          if (!processedVars.has(depVar.parent!)) return;

          combinations = expandCombinationsWithDependentVar(combinations, depVar);
          processedVars.add(depVar.name);
          addedThisIteration = true;
        });

        if (!addedThisIteration) break;
      }

      // Process automatic variables after all other variables are processed
      automaticVars.forEach(autoVar => {
        combinations = expandCombinationsWithAutomaticVar(combinations, autoVar, vars);
      });

      // Process fromList variables after all other variables are processed
      fromListVars.forEach(fromListVar => {
        combinations = expandCombinationsWithFromListVar(combinations, fromListVar);
      });

      return combinations;
    } catch (error) {
      console.error('Error generating hierarchical combinations:', error);
      return [];
    }
  };

  const generateCartesianProduct = (vars: Variable[]): any[] => {
    if (vars.length === 0) return [];
    if (vars.length === 1) {
      return vars[0].parsedValues.map((value, index) => {
        const row: any = { id: index + 1 };
        row[vars[0].name] = value;
        return row;
      });
    }

    // Optimized cartesian product using flatMap for better performance
    const valueArrays = vars.map(v => v.parsedValues);

    const combinations = valueArrays.reduce((acc, curr) => {
      return acc.flatMap(a => curr.map(c => [...a, c]));
    }, [[]]);

    return combinations.map((combination, index) => {
      const row: any = { id: index + 1 };
      vars.forEach((variable, varIndex) => {
        row[variable.name] = combination[varIndex];
      });
      return row;
    });
  };

  const expandCombinationsWithDependentVar = (existingCombinations: any[], depVar: Variable): any[] => {
    try {
      const newCombinations: any[] = [];
      let id = 1;

      existingCombinations.forEach(combo => {
        const parentValue = combo[depVar.parent!];
        const availableValues = depVar.conditions![parentValue];

        if (availableValues) {
          const values = parseValuesUtil(availableValues);

          if (values.length > 0) {
            values.forEach(value => {
              const newCombo = { ...combo, id: id++ };
              newCombo[depVar.name] = value;
              newCombinations.push(newCombo);
            });
          }
        } else {
          // Keep the existing combination but without the dependent variable
          const newCombo = { ...combo, id: id++ };
          newCombinations.push(newCombo);
        }
      });

      return newCombinations.length > 0 ? newCombinations : existingCombinations;
    } catch (error) {
      console.error('Error expanding combinations with dependent variable:', error);
      return existingCombinations;
    }
  };

  const expandCombinationsWithAutomaticVar = (existingCombinations: any[], autoVar: Variable, allVariables: Variable[]): any[] => {
    try {
      const newCombinations: any[] = [];
      let id = 1;

      existingCombinations.forEach(combo => {
        const newCombo = { ...combo, id: id++ };

        // Get variable names used in the formula
        const formulaVars = getVariableNamesFromFormula(autoVar.formula!);

        // Create a map of variable values for formula evaluation
        const variableValues: Record<string, any> = {};
        formulaVars.forEach(varName => {
          // Find the variable in all variables
          const sourceVar = allVariables.find(v => v.name === varName);
          if (sourceVar && combo[varName] !== undefined) {
            variableValues[varName] = combo[varName];
          }
        });

        // Evaluate the formula
        const result = evaluateFormula(autoVar.formula!, variableValues);
        newCombo[autoVar.name] = result;

        newCombinations.push(newCombo);
      });

      return newCombinations;
    } catch (error) {
      console.error('Error expanding combinations with automatic variable:', error);
      return existingCombinations;
    }
  };

  const expandCombinationsWithFromListVar = (existingCombinations: any[], fromListVar: Variable): any[] => {
    try {
      const newCombinations: any[] = [];
      let id = 1;

      if (!fromListVar.fromListData || !fromListVar.fromListData.valueList) {
        console.warn('FromList variable has no value list data:', fromListVar.name);
        return existingCombinations;
      }

      existingCombinations.forEach(combo => {
        // Generate composed index for this combination
        const composedIndexValue = generateComposedIndex(combo);

        // Find the value for this composed index
        const matchingEntry = fromListVar.fromListData!.valueList.find(entry =>
          entry.composedIndex === composedIndexValue
        );

        if (matchingEntry && matchingEntry.value) {
          // Expand the value (it might be a range or comma-separated)
          const expandedValues = parseValuesUtil(matchingEntry.value);

          // Create a combination for each expanded value
          expandedValues.forEach(value => {
            const newCombo = { ...combo, id: id++ };
            newCombo[fromListVar.name] = value;
            newCombinations.push(newCombo);
          });
        } else {
          // No matching entry, use default
          const newCombo = { ...combo, id: id++ };
          newCombo[fromListVar.name] = 'N/A';
          newCombinations.push(newCombo);
        }
      });

      return newCombinations;
    } catch (error) {
      console.error('Error expanding combinations with fromList variable:', error);
      return existingCombinations;
    }
  };

  const getVariableNamesFromFormula = (formula: string): string[] => {
    // Extract variable names from formula (excluding mathematical functions)
    const mathFunctions = ['sqrt', 'sin', 'cos', 'tan', 'log', 'exp', 'abs', 'pow'];
    const variableNames = [];

    // Simple regex to find potential variable names (letters, numbers, dots, underscores)
    const matches = formula.match(/\b[a-zA-Z_][a-zA-Z0-9_.]*\b/g) || [];

    for (const match of matches) {
      if (!mathFunctions.includes(match) && !match.match(/^\d+$/)) {
        variableNames.push(match);
      }
    }

    return Array.from(new Set(variableNames)); // Remove duplicates
  };

  const generateMatrixHash = async (data: any[]): Promise<string> => {
    try {
      // Handle empty matrix
      if (!data || data.length === 0) {
        return 'empty-matrix';
      }

      // Create a deterministic string representation of the matrix
      const matrixString = JSON.stringify(data, Object.keys(data[0] || {}).sort());
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(matrixString);
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex.substring(0, 16); // Return first 16 characters for display
    } catch (error) {
      console.error('Error generating matrix hash:', error);
      return 'error';
    }
  };

  // Warnings analysis
  const analyzeWarnings = useCallback(() => {
    const newWarnings: Warning[] = [];

    // Check for variables not included in matrix
    const validVariables = variables.filter(v =>
      v.type === 'automatic' || v.parsedValues.length > 0 || (v.type === 'fromList' && v.fromListData && v.fromListData.valueList.length > 0)
    );
    const excludedVariables = variables.filter(v => v.parsedValues.length === 0 && v.type !== 'automatic' && v.type !== 'fromList');

    if (excludedVariables.length > 0) {
      const independentExcluded = excludedVariables.filter(v => v.type === 'independent');
      const dependentExcluded = excludedVariables.filter(v => v.type === 'dependent');

      if (independentExcluded.length > 0) {
        newWarnings.push({
          type: 'excluded_variables',
          title: 'Independent Variables Not Included',
          description: 'These independent variables have no values defined and are excluded from the matrix.',
          variables: independentExcluded.map(v => ({
            name: v.name,
            issue: 'No values defined'
          })),
          severity: 'warning'
        });
      }

      if (dependentExcluded.length > 0) {
        newWarnings.push({
          type: 'excluded_dependent',
          title: 'Dependent Variables Not Included',
          description: 'These dependent variables are excluded from the matrix due to missing rules or invalid parent relationships.',
          variables: dependentExcluded.map(v => ({
            name: v.name,
            issue: v.parent ? 'No dependency rules defined' : 'No parent variable selected'
          })),
          severity: 'warning'
        });
      }
    }

    // Check for dependent variables with missing parent values
    const dependentVars = variables.filter(v => v.type === 'dependent' && v.parsedValues.length > 0);
    dependentVars.forEach(depVar => {
      const parentVar = variables.find(v => v.name === depVar.parent);
      if (parentVar) {
        const missingParentValues = parentVar.parsedValues.filter(parentValue =>
          !depVar.conditions![parentValue]
        );

        if (missingParentValues.length > 0) {
          newWarnings.push({
            type: 'missing_rules',
            title: `Missing Rules for ${depVar.name}`,
            description: `This dependent variable has no rules defined for some parent values.`,
            variables: [{
              name: depVar.name,
              issue: `Missing rules for parent values: ${missingParentValues.join(', ')}`
            }],
            severity: 'warning'
          });
        }
      }
    });

    // Check for fromList variables with incomplete mappings
    if (composedIndexConfig.enabled && matrixData.length > 0) {
      const fromListVars = variables.filter(v => v.type === 'fromList' && v.fromListData);

      fromListVars.forEach(fromListVar => {
        if (!fromListVar.fromListData || !fromListVar.fromListData.valueList) {
          return;
        }

        // Get all unique composed index values from the matrix
        const allComposedIndexValues = new Set();
        matrixData.forEach(row => {
          const composedIndex = generateComposedIndex(row);
          if (composedIndex) {
            allComposedIndexValues.add(composedIndex);
          }
        });

        // Get composed index values that have mappings in the fromList variable
        const mappedComposedIndexes = new Set(
          fromListVar.fromListData.valueList
            .map(item => item.composedIndex)
            .filter(idx => idx && idx.trim() !== '')
        );

        // Find missing mappings
        const missingMappings: string[] = [];
        const extraMappings: string[] = [];

        allComposedIndexValues.forEach(composedIndex => {
          if (!mappedComposedIndexes.has(composedIndex as string)) {
            missingMappings.push(composedIndex as string);
          }
        });

        mappedComposedIndexes.forEach(composedIndex => {
          if (!allComposedIndexValues.has(composedIndex)) {
            extraMappings.push(composedIndex as string);
          }
        });

        // Add warning for missing mappings
        if (missingMappings.length > 0) {
          const missingCount = missingMappings.length;
          const totalCount = allComposedIndexValues.size;
          const issueText = missingCount === totalCount
            ? 'No values provided for any composed index'
            : `Missing values for ${missingCount} out of ${totalCount} composed index values`;

          newWarnings.push({
            type: 'incomplete_fromlist_mapping',
            title: `Incomplete Mapping for "${fromListVar.name}"`,
            description: 'This "From List" variable is missing values for some composed index combinations. Missing mappings will result in "N/A" values in the matrix.',
            variables: [{
              name: fromListVar.name,
              issue: `${issueText}. Missing: ${missingMappings.slice(0, 5).join(', ')}${missingMappings.length > 5 ? ` and ${missingMappings.length - 5} more` : ''}`
            }],
            severity: 'warning'
          });
        }
      });
    }

    setWarnings(newWarnings);
  }, [variables, composedIndexConfig, matrixData]);

  // Variable management
  const handleAddVariable = () => {
    const newVariable: Variable = {
      id: Date.now().toString() + Math.random().toString(),
      name: '',
      type: 'independent',
      values: '',
      parsedValues: [],
      description: '',
      imported: false
    };

    setVariables(prev => [...prev, newVariable]);
    setEditingVariableId(newVariable.id);
    setVariableModalOpen(true);
  };

  const handleEditVariable = (variableId: string) => {
    // Reset verification when starting to edit an existing variable
    const existingVariable = variables.find(v => v.id === variableId);
    if (existingVariable) {
      setVariablesVerified(false);
      setVerificationDetails({});
      // Clear localStorage when verification is reset
      try {
        const key = getVerificationStorageKey();
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error clearing verification from localStorage:', error);
      }
    }

    setEditingVariableId(variableId);
    setVariableModalOpen(true);
  };

  const handleDeleteVariable = (variableId: string) => {
    setVariables(prev => prev.filter(v => v.id !== variableId));
    setEditingVariableId(null);
    setVariableModalOpen(false);
  };

  const handleSaveVariable = (updatedVariable: Variable) => {
    setVariables(prev => prev.map(v =>
      v.id === updatedVariable.id ? updatedVariable : v
    ));

    // Update parsed values based on type
    if (updatedVariable.type === 'independent' && updatedVariable.values) {
      updatedVariable.parsedValues = parseValues(updatedVariable.values);
    } else if (updatedVariable.type === 'dependent') {
      // Calculate parsed values from conditions
      const allValues = new Set();
      if (updatedVariable.conditions) {
        Object.values(updatedVariable.conditions).forEach(conditionValues => {
          parseValues(conditionValues).forEach(val => allValues.add(val));
        });
      }
      updatedVariable.parsedValues = Array.from(allValues);
    } else if (updatedVariable.type === 'automatic') {
      updatedVariable.parsedValues = [];
    } else if (updatedVariable.type === 'fromList' && updatedVariable.fromListData) {
      // Populate parsedValues with unique values from the value list
      const uniqueValues = Array.from(new Set(updatedVariable.fromListData!.valueList.map(item => item.value).filter(val => val && val.trim() !== '')));
      updatedVariable.parsedValues = uniqueValues;
    }

    setEditingVariableId(null);
    setVariableModalOpen(false);
  };

  // Sorting and filtering
  const handleSort = (column: string) => {
    const newDirection = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';
    setSortState({ column, direction: newDirection });

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[column];
      const bValue = b[column];

      // Handle numeric sorting
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);

      let compare = 0;
      if (!isNaN(aNum) && !isNaN(bNum)) {
        compare = aNum - bNum;
      } else {
        compare = String(aValue).localeCompare(String(bValue));
      }

      return newDirection === 'asc' ? compare : -compare;
    });

    setFilteredData(sorted);
  };

  const handleFilter = (column: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [column]: value }));

    if (Object.values({ ...filterValues, [column]: value }).every(v => !v)) {
      // No filters applied
      setFilteredData(matrixData);
    } else {
      const filtered = matrixData.filter(row => {
        for (const [col, filterValue] of Object.entries({ ...filterValues, [column]: value })) {
          if (filterValue) {
            const rowValue: string = row[col] ? String(row[col]) : '';
            if (!rowValue.toLowerCase().includes(filterValue.toLowerCase())) {
              return false;
            }
          }
        }
        return true;
      });
      setFilteredData(filtered);
    }
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilterValues({});
    setFilteredData(matrixData);
    setCurrentPage(0);
  };

  // Column reordering functions
  const handleDragStart = (column: string) => {
    setDraggedColumn(column);
  };

  const handleDragOver = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    setDragOverColumn(column);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: string) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn === targetColumn) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    const draggedIndex = matrixColumns.indexOf(draggedColumn);
    const targetIndex = matrixColumns.indexOf(targetColumn);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    const newColumns = [...matrixColumns];
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, removed);

    setMatrixColumns(newColumns);
    setDraggedColumn(null);
    setDragOverColumn(null);

    // Update column order for export
    const variableColumns = newColumns.filter(col => col !== composedIndexConfig.name);
    setColumnOrder(variableColumns);
  };

  // Export functions
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = matrixColumns;
    const csvContent = [
      `# Matrix Export - Generated: ${new Date().toISOString()}`,
      `# Matrix Hash: ${matrixHash}`,
      `# Total Combinations: ${filteredData.length}`,
      ...(composedIndexConfig.enabled ? [`# Composed Index: ${composedIndexConfig.name} (${composedIndexConfig.selectedVariables.length} variables)`] : []),
      '',
      headers.join(','),
      ...filteredData.map(row => headers.map(col => `"${row[col] || ''}"`).join(','))
    ].join('\n');

    downloadFile(csvContent, `matrix_${Date.now()}.csv`, 'text/csv');
    setSuccess('CSV export completed');
  };

  const exportConfiguration = () => {
    const config: LCMConfig = {
      variables,
      composedIndex: composedIndexConfig,
      columnOrder,
      version: "1.0",
      exported: new Date().toISOString(),
      matrixHash,
      yaml_template: yamlTemplate,
      verification: {
        status: variablesVerified,
        verifiedBy: variablesVerified ? user?.name : undefined,
        verifiedAt: variablesVerified ? verificationDetails.verifiedAt : undefined
      }
    };

    const configContent = JSON.stringify(config, null, 2);
    downloadFile(configContent, `lcm_config_${Date.now()}.json`, 'application/json');
    setSuccess('Configuration exported');
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    openFilePreviewOrDownload(blob, filename, mimeType);
  };

  // Utility function to preprocess YAML template for section-specific variables
  const preprocessYamlTemplate = (yamlTemplate: string, variables: Variable[]): string => {
    // Find all section headers like "Wave1:", "Wave2:", etc.
    const sectionRegex = /^(\w+):/gm;
    const sections: { name: string; start: number; end: number }[] = [];
    let match;
    while ((match = sectionRegex.exec(yamlTemplate)) !== null) {
      sections.push({ name: match[1], start: match.index, end: -1 });
    }

    // Set end positions
    for (let i = 0; i < sections.length - 1; i++) {
      sections[i].end = sections[i + 1].start;
    }
    if (sections.length > 0) {
      sections[sections.length - 1].end = yamlTemplate.length;
    }

    let processedTemplate = yamlTemplate;

    // For each section
    sections.forEach(section => {
      const sectionName = section.name;
      const sectionText = yamlTemplate.substring(section.start, section.end);

      // Find variables that start with sectionName + '_'
      const sectionVars = variables.filter(v => v.name.startsWith(sectionName + '_'));

      if (sectionVars.length > 0) {
        let modifiedSection = sectionText;

        // For each section var, replace %suffix with %fullName
        sectionVars.forEach(varItem => {
          const suffix = varItem.name.substring(sectionName.length + 1); // after '_'
          const placeholder = '%' + suffix;
          const newPlaceholder = '%' + varItem.name;
          // Replace only in this section
          modifiedSection = modifiedSection.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), newPlaceholder);
        });

        // Replace the section in the template
        processedTemplate = processedTemplate.replace(sectionText, modifiedSection);
      }
    });

    return processedTemplate;
  };

  // Generate Matrix
  const handleGenerateMatrix = async () => {
    if (variables.length === 0) {
      setError('Please add at least one variable before generating matrix');
      return;
    }

    try {
      setMatrixGenerationLoading(true);

      // Deep clone and parse variables before sending to the backend
      const parsedVariables = JSON.parse(JSON.stringify(variables));
      parsedVariables.forEach((variable: Variable) => {
        if (variable.type === 'dependent' && variable.conditions) {
          for (const key in variable.conditions) {
            variable.conditions[key] = parseValuesUtil(variable.conditions[key]).join(',');
          }
        }
      });


      // Send LCM config to backend - matrix generation will happen on the server
      const lcmConfig = {
        variables: parsedVariables,
        composedIndex: composedIndexConfig,
        columnOrder: columnOrder
      };

      const response = await sandboxesService.generateMatrix({
        lcm_config: lcmConfig,
        project_id: projectId,
        revision_id: revisionId,
        sandbox_id: sandboxId,
        lcm_id: lcmId
      });

      setMatrixGenerationTask({
        task_id: response.task_id,
        status: response.status,
        progress: 0
      });

      // Track the current matrix task ID for cleanup purposes
      setCurrentMatrixTaskId(response.task_id);

      setSuccess('Matrix generation started successfully');

      // Start polling for matrix task status
      pollMatrixTaskStatus(response.task_id);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start matrix generation');
      setMatrixGenerationLoading(false);
    }
  };

  // Generate YMLs
  const handleGenerateYMLs = async () => {
    if (!yamlTemplate) {
      setError('A YAML template is required for YML generation');
      return;
    }

    try {
      setYmlGenerationLoading(true);

      console.log('[FE] Starting YML generation process');

      // Check memory before starting
      if ('performance' in window && 'memory' in (window.performance as any)) {
        const memory = (window.performance as any).memory;
        const usedHeap = (memory.usedJSHeapSize / 1024 / 1024).toFixed(2);
        const totalHeap = (memory.totalJSHeapSize / 1024 / 1024).toFixed(2);
        console.log(`[FE] Memory before YML generation: ${usedHeap} MB / ${totalHeap} MB`);
      }

      // Get model name from sandbox
      const modelName = sandbox?.file_path ? sandbox.file_path.split('\\').pop() || sandbox.name : 'default_model';

      // Send LCM config to backend - matrix generation will happen on the server
      const lcmConfig = {
        variables: variables,
        composedIndex: composedIndexConfig,
        columnOrder: columnOrder
      };

      console.log(`[FE] Sending YML generation request with ${variables.length} variables`);

      const response = await sandboxesService.generateYMLs({
        lcm_config: lcmConfig,
        yaml_template: yamlTemplate,
        model_name: modelName,
        project_id: projectId,
        revision_id: revisionId,
        sandbox_id: sandboxId,
        lcm_id: lcmId
      });

      console.log(`[FE] YML generation task created: ${response.task_id}`);

      setYmlGenerationTask({
        task_id: response.task_id,
        status: response.status,
        progress: 0
      });

      setSuccess('YML generation started successfully');

      // Start polling for task status
      pollTaskStatus(response.task_id);

    } catch (err: any) {
      console.error('[FE] YML generation failed:', err);
      setError(err.response?.data?.error || 'Failed to start YML generation');
    } finally {
      setYmlGenerationLoading(false);
    }
  };

  // Poll task status
  const pollTaskStatus = async (taskId: string) => {
    try {
      console.log(`[FE] Polling task status for ${taskId}`);

      // Check memory during polling
      if ('performance' in window && 'memory' in (window.performance as any)) {
        const memory = (window.performance as any).memory;
        const usedHeap = (memory.usedJSHeapSize / 1024 / 1024).toFixed(2);
        console.log(`[FE] Memory during polling: ${usedHeap} MB`);
      }

      const statusData = await sandboxesService.getTaskStatus(taskId);

      console.log(`[FE] Task ${taskId} status: ${statusData.status}, progress: ${statusData.progress_percentage || 0}%`);

      setYmlGenerationTask(prev => prev ? {
        ...prev,
        status: statusData.status,
        progress: statusData.progress_percentage || 0,
        result_files: statusData.result_files
      } : null);

      // Continue polling if not completed or failed
      if (statusData.status === 'processing') {
        setTimeout(() => pollTaskStatus(taskId), 2000); // Poll every 2 seconds
      } else if (statusData.status === 'completed') {
        console.log(`[FE] Task ${taskId} completed successfully`);
        if (statusData.result_files) {
          console.log(`[FE] Result files available: ${statusData.result_files.length} files`);
          statusData.result_files.forEach((file: any) => {
            console.log(`[FE]   - ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          });
        }
      } else if (statusData.status === 'failed') {
        console.error(`[FE] Task ${taskId} failed: ${statusData.error_message}`);
      }
    } catch (error) {
      console.error('[FE] Failed to poll task status:', error);
    }
  };

  // Poll matrix task status
  const pollMatrixTaskStatus = async (taskId: string) => {
    try {
      const statusData = await sandboxesService.getTaskStatus(taskId);
      setMatrixGenerationTask(prev => prev ? {
        ...prev,
        status: statusData.status,
        progress: statusData.progress_percentage || 0,
        result_files: statusData.result_files
      } : null);

      // Continue polling if not completed or failed
      if (statusData.status === 'processing') {
        setTimeout(() => pollMatrixTaskStatus(taskId), 2000); // Poll every 2 seconds
      } else if (statusData.status === 'completed') {
        // Fetch matrix data and update display
        try {
          const matrixDataResponse = await sandboxesService.getMatrixData(taskId);
          setMatrixData(matrixDataResponse.matrix_data);
          setMatrixColumns(matrixDataResponse.matrix_columns);
          setMatrixHash(matrixDataResponse.matrix_hash);
          setFilteredData(matrixDataResponse.matrix_data);

          // Analyze warnings
          analyzeWarnings();

          setSuccess(`Matrix generated successfully with ${matrixDataResponse.matrix_data.length.toLocaleString()} combinations`);
        } catch (error) {
          console.error('Failed to fetch matrix data:', error);
          setError('Failed to retrieve generated matrix');
        }

        // Clear matrix generation task state to hide status
        setMatrixGenerationTask(null);
        setMatrixGenerationLoading(false);
      } else if (statusData.status === 'failed') {
        setError('Matrix generation failed. Please try again.');
        setMatrixGenerationTask(null);
        setMatrixGenerationLoading(false);
      }
    } catch (error) {
      console.error('Failed to poll matrix task status:', error);
      setMatrixGenerationTask(null);
      setMatrixGenerationLoading(false);
    }
  };

  // Download file - fetch via API and preview in a new tab
  const handleDownloadFile = async (filename: string) => {
    const taskId = ymlGenerationTask?.task_id;
    if (!taskId) {
      setError('No task ID available for download');
      return;
    }

    try {
      console.log(`[FE] Starting preview fetch for ${filename}`);
      const blob = await sandboxesService.downloadTaskFile(taskId, filename);
      openFilePreviewOrDownload(blob, filename);
      setSuccess(`Opened preview: ${filename}`);
    } catch (error: any) {
      console.error('Download failed:', error);
      setError(`Failed to open ${filename}: ${error.message}`);
    }
  };

  // DB export
  const handleExportToDB = async (providedName?: string) => {
    const nameToUse = providedName || exportName;

    if (!nameToUse.trim()) {
      setError('Please enter a name for the LCM');
      return;
    }

    if (!sandbox || !projectId || !revisionId) {
      setError('No sandbox context available for export');
      return;
    }

    try {
      // Preprocess YAML template for section-specific variables and replace %BallastCaseFile
      const modelName = sandbox.file_path ? sandbox.file_path.split('\\').pop() || sandbox.name : sandbox.name;
      const processedYamlTemplate = preprocessYamlTemplate(yamlTemplate, variables).replace(/%BallastCaseFile/g, modelName);

      // Deep clone and parse variables before sending to the backend
      const parsedVariables = JSON.parse(JSON.stringify(variables));
      parsedVariables.forEach((variable: Variable) => {
        if (variable.type === 'dependent' && variable.conditions) {
          for (const key in variable.conditions) {
            variable.conditions[key] = parseValuesUtil(variable.conditions[key]).join(',');
          }
        }
      });

      const configData = {
        variables: parsedVariables.map((v: Variable) => ({
          id: v.id,
          name: v.name,
          type: v.type,
          values: v.values,
          formula: v.formula,
          parent: v.parent,
          conditions: v.conditions,
          fromListData: v.fromListData,
          description: v.description
        })),
        composedIndex: composedIndexConfig,
        columnOrder,
        total_load_cases: matrixData.length,
        matrix_hash: matrixHash,
        yaml_template: processedYamlTemplate,
        verification: {
          status: variablesVerified,
          verifiedBy: variablesVerified ? user?.name : undefined,
          verifiedAt: variablesVerified ? new Date().toISOString() : undefined
        }
      };

      let lcmId: number;
      if (currentLcm) {
        // Update existing LCM
        await sandboxesService.updateLCM(
          parseInt(projectId),
          parseInt(revisionId),
          sandbox.id,
          currentLcm.id,
          {
            prepro_name: nameToUse,
            prepro_data: configData,
          }
        );
        lcmId = currentLcm.id;
        setSuccess('LCM updated successfully');
      } else {
        // Create new LCM
        const result = await sandboxesService.createLCM(
          parseInt(projectId),
          parseInt(revisionId),
          sandbox.id,
          {
            prepro_name: nameToUse,
            prepro_data: configData,
          }
        );
        lcmId = result.lcm_id;
        setSuccess('LCM created successfully');
      }

      // Clean up previous matrix generation tasks (keep only the current one)
      try {
        const cleanupResult = await sandboxesService.cleanupMatrices({
          current_task_id: currentMatrixTaskId || undefined,
          lcm_id: lcmId
        });
        console.log(`Cleaned up ${cleanupResult.deleted_count} previous matrix tasks`);
      } catch (cleanupError) {
        console.warn('Failed to cleanup previous matrices:', cleanupError);
        // Don't fail the export if cleanup fails
      }

      // Update initial config to reflect saved state
      setInitialConfig(getCurrentConfig());

      // Only close dialog and reset if it was opened (i.e., no providedName)
      if (!providedName) {
        setExportDialogOpen(false);
        setExportName('');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to export LCM');
    }
  };

  // Template loading
  const loadTemplate = (templateKey: string) => {
    const template = templates[templateKey];
    if (!template) {
      setError(`Template "${templateKey}" not found`);
      return;
    }

    const templateVariables: Variable[] = template.variables.map((tv: any) => ({
      id: Date.now().toString() + Math.random().toString(),
      name: tv.name,
      type: tv.type as Variable['type'],
      values: tv.values || '',
      formula: tv.formula || '',
      parent: tv.parent || '',
      conditions: (tv.conditions as Record<string, string>) || {},
      parsedValues: tv.type === 'independent' ? parseValues(tv.values || '') : [],
      description: tv.description || '',
      imported: false
    }));

    setVariables(templateVariables);
    setTemplateSelectionOpen(false);
    setSuccess(`Template "${template.name}" loaded successfully`);
    setInitialConfig(getCurrentConfig());
  };

  const canManageLCMs = hasCapability(user, CAP.MANAGE_LCMS);

  // Helper function to open dependency rules modal
  const openDependencyRulesModal = (variable: Variable) => {
    if (!variable || !variable.parent) {
      setError('Variable not found or not a dependent variable');
      return;
    }

    setCurrentRulesVariable(variable);
    setDependencyRulesModalOpen(true);

    // Initialize rules data
    const parentVar = variables.find(v => v.name === variable.parent);
    if (parentVar && parentVar.parsedValues && parentVar.parsedValues.length > 0) {
      const rulesData = parentVar.parsedValues.map(parentValue => ({
        parentValue: String(parentValue),
        childValues: variable.conditions?.[parentValue] || ''
      }));
      setDependencyRulesData(rulesData);
    } else {
      setDependencyRulesData([]);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading Overlay */}
      {matrixGenerationLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <LoadingAnimation />
          </Box>
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            LCM Lab
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Create and configure Load Case Matrices with advanced variable dependencies
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FolderIcon />}
            onClick={() => setTemplateSelectionOpen(true)}
            aria-label="Load template"
          >
            Templates
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={(e) => setLoadMenuAnchor(e.currentTarget)}
            aria-label="Load from"
          >
            Load from
          </Button>
          <Button
            variant={yamlTemplate ? "contained" : "outlined"}
            startIcon={<CodeIcon />}
            onClick={() => {
              if (yamlTemplate) {
                setYamlDisplayOpen(true);
              } else {
                setYamlImportOpen(true);
              }
            }}
            aria-label={yamlTemplate ? "View YAML" : "Import YAML"}
            sx={{
              ...(yamlTemplate && {
                backgroundColor: '#e8f5e8',
                color: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#d4edda',
                }
              })
            }}
          >
            {yamlTemplate ? 'YAML imported' : 'YAML Import'}
          </Button>
          <Button
            variant={variablesVerified ? "contained" : "outlined"}
            startIcon={<CheckCircleIcon />}
            onClick={() => setVerificationModalOpen(true)}
            aria-label="Verify variables"
            sx={{
              ...(variablesVerified && {
                backgroundColor: '#e8f5e8',
                color: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#d4edda',
                }
              })
            }}
          >
            {variablesVerified ? 'Verified' : 'Verify Variables'}
          </Button>
          {projectId && revisionId && sandbox && canManageLCMs && (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  if (currentLcm) {
                    handleExportToDB(currentLcm.prepro_name);
                  } else {
                    setExportDialogOpen(true);
                  }
                }}
                color="secondary"
                aria-label="Export to database"
                disabled={!variablesVerified}
              >
                Export to DB
              </Button>
              {variables.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<GroupWorkIcon />}
                  onClick={handleGenerateMatrix}
                  color="secondary"
                  aria-label="Generate matrix"
                  disabled={!variablesVerified || matrixGenerationLoading}
                >
                  {matrixGenerationLoading ? 'Generating...' : 'Generate Matrix'}
                </Button>
              )}
              {matrixData.length > 0 && yamlTemplate && (
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleGenerateYMLs}
                  color="primary"
                  aria-label="Generate YML files"
                  disabled={!variablesVerified || ymlGenerationLoading}
                >
                  {ymlGenerationLoading ? 'Starting...' : 'Generate YMLs'}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Load Menu */}
      <Menu
        anchorEl={loadMenuAnchor}
        open={Boolean(loadMenuAnchor)}
        onClose={() => setLoadMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setLoadMenuAnchor(null); setLoadConfigOpen(true); }}>
          JSON Config
        </MenuItem>
        <MenuItem onClick={() => { setLoadMenuAnchor(null); setLoadCsvOpen(true); }}>
          CSV/XLSX File
        </MenuItem>
      </Menu>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} role="alert">
          {success}
        </Alert>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setWarningsModalOpen(true)}>
              View Details
            </Button>
          }
        >
          {warnings.length} warning{warnings.length > 1 ? 's' : ''} detected
        </Alert>
      )}

      {/* YML Generation Status and Downloads */}
      {ymlGenerationTask && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            YML Generation Status
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Task ID: {ymlGenerationTask.task_id}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Status: <Chip
                label={ymlGenerationTask.status}
                color={
                  ymlGenerationTask.status === 'completed' ? 'success' :
                    ymlGenerationTask.status === 'failed' ? 'error' :
                      ymlGenerationTask.status === 'processing' ? 'warning' : 'default'
                }
                size="small"
              />
            </Typography>

            {ymlGenerationTask.status === 'processing' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Progress: {ymlGenerationTask.progress || 0}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={ymlGenerationTask.progress || 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
          </Box>

          {/* Download Links */}
          {ymlGenerationTask.status === 'completed' && ymlGenerationTask.result_files && ymlGenerationTask.result_files.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Download Files
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {ymlGenerationTask.result_files.map((file: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={() => handleDownloadFile(file.name)}
                      size="small"
                    >
                      {file.name}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {ymlGenerationTask.status === 'failed' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              YML generation failed. Please try again.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: 3, mb: 3 }}>
        {/* Configuration Panel */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" component="h2">
                Variable Configuration
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddVariable}
                aria-label="Add variable"
              >
                Add Variable
              </Button>
            </Box>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {variables.map((variable) => (
                <Card key={variable.id} sx={{ mb: 1, p: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {variable.name || 'Unnamed Variable'}
                    </Typography>
                    <Box>
                      <Chip
                        label={variable.type}
                        size="small"
                        color={
                          variable.type === 'independent' ? 'success' :
                            variable.type === 'dependent' ? 'warning' :
                              variable.type === 'automatic' ? 'info' : 'secondary'
                        }
                        sx={{ mr: 0.5, fontSize: '0.7rem', height: 20 }}
                      />
                      {variable.imported && (
                        <Chip
                          label="imported"
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEditVariable(variable.id)}
                        aria-label={`Edit ${variable.name}`}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteVariable(variable.id)}
                        aria-label={`Delete ${variable.name}`}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {variable.type === 'independent' && `${variable.parsedValues.length} values`}
                    {variable.type === 'dependent' && `Depends on: ${variable.parent}`}
                    {variable.type === 'automatic' && `Formula: ${variable.formula}`}
                    {variable.type === 'fromList' && `${variable.fromListData?.valueList.length || 0} mappings`}
                  </Typography>
                </Card>
              ))}

              {variables.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No variables defined. Click "Add Variable" to get started.
                </Typography>
              )}
            </Box>

            {/* Composed Index Configuration */}
            <Box mt={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={composedIndexConfig.enabled}
                    onChange={(e) => setComposedIndexConfig(prev => ({
                      ...prev,
                      enabled: e.target.checked
                    }))}
                  />
                }
                label="Enable Composed Index"
              />

              {composedIndexConfig.enabled && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => setComposedIndexModalOpen(true)}
                  sx={{ mt: 1 }}
                >
                  Configure Composed Index
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Matrix Display */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" component="h2">
                Generated Matrix
              </Typography>
              <Box>
                {matrixData.length > 0 && (
                  <>
                    <Chip
                      label={`${matrixData.length.toLocaleString()} combinations`}
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`Hash: ${matrixHash}`}
                      size="small"
                      variant="outlined"
                    />
                  </>
                )}
              </Box>
            </Box>

            {matrixData.length > 0 ? (
              <>
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: matrixColumns.length * 120 }}>
                    <TableHead>
                      <TableRow>
                        {matrixColumns.map((column) => (
                          <TableCell
                            key={column}
                            sx={{
                              fontWeight: 'bold',
                              minWidth: 120,
                              cursor: 'grab',
                              userSelect: 'none',
                              backgroundColor: draggedColumn === column ? 'action.selected' :
                                dragOverColumn === column ? 'action.hover' : 'inherit',
                              opacity: draggedColumn === column ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: draggedColumn ? 'inherit' : 'action.hover'
                              }
                            }}
                            draggable
                            onDragStart={() => handleDragStart(column)}
                            onDragOver={(e) => handleDragOver(e, column)}
                            onDrop={(e) => handleDrop(e, column)}
                            onDragEnd={handleDragEnd}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <DragHandleIcon
                                sx={{
                                  color: 'text.secondary',
                                  cursor: 'grab',
                                  '&:hover': { color: 'text.primary' }
                                }}
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {column}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSort(column);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <SortIcon
                                  sx={{
                                    transform: sortState.column === column && sortState.direction === 'desc' ? 'rotate(180deg)' : 'none',
                                    color: sortState.column === column ? 'primary.main' : 'text.secondary'
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                      {/* Filter Row */}
                      <TableRow>
                        {matrixColumns.map((column) => (
                          <TableCell key={`filter-${column}`} sx={{ py: 1 }}>
                            <TextField
                              size="small"
                              placeholder={`Filter ${column}`}
                              value={filterValues[column] || ''}
                              onChange={(e) => handleFilter(column, e.target.value)}
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)
                        .map((row, index) => (
                          <TableRow key={currentPage * rowsPerPage + index}>
                            {matrixColumns.map((column) => (
                              <TableCell key={column} sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {row[column] !== undefined ? String(row[column]) : ''}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Table Controls */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <TablePagination
                      component="div"
                      count={filteredData.length}
                      page={currentPage}
                      onPageChange={(event, newPage) => setCurrentPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setCurrentPage(0);
                      }}
                      rowsPerPageOptions={[50, 100, 200, 500]}
                      labelRowsPerPage="Rows per page:"
                    />
                    {Object.values(filterValues).some(v => v) && (
                      <Button
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportToCSV}
                      disabled={filteredData.length === 0}
                    >
                      Export CSV
                    </Button>
                    <Button
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportConfiguration}
                    >
                      Export Config
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box textAlign="center" py={8}>
                <TableChartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Matrix Generated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure variables and click "Generate Matrix" to create your load case matrix.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Variable Modal */}
      <Dialog open={variableModalOpen} onClose={() => setVariableModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVariableId ? 'Edit Variable' : 'Add New Variable'}
        </DialogTitle>
        <DialogContent>
          <VariableModal
            variable={variables.find(v => v.id === editingVariableId)}
            allVariables={variables}
            composedIndexConfig={composedIndexConfig}
            matrixData={matrixData}
            onSave={handleSaveVariable}
            onCancel={() => setVariableModalOpen(false)}
            onOpenDependencyRules={openDependencyRulesModal}
            generateComposedIndex={generateComposedIndex}
          />
        </DialogContent>
      </Dialog>

      {/* Composed Index Modal */}
      <Dialog open={composedIndexModalOpen} onClose={() => setComposedIndexModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configure Composed Index</DialogTitle>
        <DialogContent>
          <ComposedIndexModal
            config={composedIndexConfig}
            variables={variables}
            onSave={(config) => {
              setComposedIndexConfig(config);
              setComposedIndexModalOpen(false);
            }}
            onCancel={() => setComposedIndexModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Warnings Modal */}
      <Dialog open={warningsModalOpen} onClose={() => setWarningsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Configuration Warnings
          </Box>
        </DialogTitle>
        <DialogContent>
          <WarningsModal warnings={warnings} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Modal */}
      <Dialog open={templateSelectionOpen} onClose={() => setTemplateSelectionOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Load Template</DialogTitle>
        <DialogContent>
          <TemplateSelectionModal
            templates={templates}
            onSelectTemplate={loadTemplate}
            onCancel={() => setTemplateSelectionOpen(false)}
          />
        </DialogContent>
      </Dialog>


      {/* Load Config Dialog */}
      <Dialog open={loadConfigOpen} onClose={() => setLoadConfigOpen(false)}>
        <DialogTitle>Load Configuration</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  try {
                    setIsLoadingConfig(true);
                    const config: LCMConfig = JSON.parse(e.target?.result as string);
                    const loadedVariables = (config.variables || []).map(v => {
                      const variable: Variable = {
                        id: v.id,
                        name: v.name,
                        type: v.type,
                        values: v.values || '',
                        formula: v.formula || '',
                        parent: v.parent || '',
                        conditions: v.conditions || {},
                        fromListData: v.fromListData,
                        description: v.description || '',
                        parsedValues: [],
                        imported: false
                      };

                      // Parse values based on type
                      if (variable.type === 'independent' && variable.values) {
                        variable.parsedValues = parseValues(variable.values);
                      } else if (variable.type === 'dependent') {
                        // Calculate parsed values from conditions
                        const allValues = new Set();
                        if (variable.conditions) {
                          Object.values(variable.conditions).forEach(conditionValues => {
                            parseValues(conditionValues).forEach(val => allValues.add(val));
                          });
                        }
                        variable.parsedValues = Array.from(allValues);
                      } else if (variable.type === 'automatic') {
                        variable.parsedValues = [];
                      } else if (variable.type === 'fromList' && variable.fromListData) {
                        // Populate parsedValues with unique values from the value list
                        const uniqueValues = Array.from(new Set(variable.fromListData!.valueList.map(item => item.value).filter(val => val && val.trim() !== '')));
                        variable.parsedValues = uniqueValues;
                      }

                      return variable;
                    });
                    setVariables(loadedVariables);
                    setComposedIndexConfig(config.composedIndex || {
                      enabled: false,
                      name: 'CaseID',
                      separator: '_',
                      selectedVariables: []
                    });
                    setColumnOrder(config.columnOrder || []);
                    setYamlTemplate(config.yaml_template || '');

                    // Load verification data
                    if (config.verification) {
                      setVariablesVerified(config.verification.status || false);
                      setVerificationDetails({
                        verifiedBy: config.verification.verifiedBy,
                        verifiedAt: config.verification.verifiedAt
                      });
                    } else {
                      setVariablesVerified(false);
                      setVerificationDetails({});
                    }

                    setLoadConfigOpen(false);
                    setSuccess('Configuration loaded successfully');
                    setInitialConfig(getCurrentConfig());
                    setIsLoadingConfig(false);
                  } catch (err) {
                    setError('Failed to load configuration file');
                  }
                };
                reader.readAsText(file);
              }
            }}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadConfigOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Load CSV/XLSX Dialog */}
      <Dialog open={loadCsvOpen} onClose={() => setLoadCsvOpen(false)}>
        <DialogTitle>Load from CSV/XLSX File</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  try {
                    let rows: any[][] = [];

                    if (file.name.endsWith('.csv')) {
                      // Parse CSV
                      const csvText = e.target?.result as string;
                      const parsed = Papa.parse(csvText, { skipEmptyLines: true });
                      rows = parsed.data as any[][];
                    } else {
                      // Parse XLSX
                      const workbook = XLSX.read(e.target?.result, { type: 'binary' });
                      const sheetName = workbook.SheetNames[0];
                      const worksheet = workbook.Sheets[sheetName];
                      rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
                    }

                    if (rows.length < 2) {
                      setError('File must have at least 2 rows (header + data)');
                      return;
                    }

                    // Create variables from columns
                    const variables: Variable[] = [];
                    const numColumns = rows[0].length;

                    for (let colIndex = 0; colIndex < numColumns; colIndex++) {
                      const varName = rows[0][colIndex]?.toString().trim();
                      if (!varName) continue;

                      const values = [];
                      for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
                        const value = rows[rowIndex][colIndex]?.toString().trim();
                        if (value) values.push(value);
                      }

                      if (values.length > 0) {
                        const valuesString = detectArithmeticSequence(values);
                        variables.push({
                          id: Date.now().toString() + colIndex.toString(),
                          name: varName,
                          type: 'independent',
                          values: valuesString,
                          parsedValues: values,
                          description: `Imported from ${file.name}`,
                          imported: true
                        });
                      }
                    }

                    setVariables(variables);
                    setLoadCsvOpen(false);
                    setSuccess(`Configuration loaded successfully with ${variables.length} variables`);
                  } catch (err) {
                    setError('Failed to load file');
                  }
                };
                if (file.name.endsWith('.csv')) {
                  reader.readAsText(file);
                } else {
                  reader.readAsBinaryString(file);
                }
              }
            }}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadCsvOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>


      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export to Database</DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleExportToDB(); }}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="LCM Name"
              fullWidth
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
              helperText={currentLcm ? "Leave empty to keep current name" : "Enter a name for the new LCM"}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!exportName.trim() && !currentLcm}
            >
              {currentLcm ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* YAML Import Dialog */}
      <Dialog open={yamlImportOpen} onClose={() => setYamlImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>YAML Template Import</DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); setYamlTemplate(yamlContent); setYamlImportOpen(false); setYamlContent(''); setSuccess('YAML template imported successfully'); }}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="YAML Template Content"
              fullWidth
              multiline
              rows={15}
              value={yamlContent}
              onChange={(e) => setYamlContent(e.target.value)}
              placeholder="Paste your YAML template here. Use %variableName for placeholders."
              helperText="Variables should be in %variableName format"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setYamlImportOpen(false);
              setYamlContent('');
            }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!yamlContent.trim()}
            >
              Import Template
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* YAML Display Dialog */}
      <Dialog open={yamlDisplayOpen} onClose={() => setYamlDisplayOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>YAML Template Content</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={yamlTemplate}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setYamlDisplayOpen(false)}>Close</Button>
          <Button onClick={() => {
            setYamlDisplayOpen(false);
            setYamlImportOpen(true);
          }} variant="outlined">
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dependency Rules Modal */}
      <Dialog open={dependencyRulesModalOpen} onClose={() => setDependencyRulesModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GroupWorkIcon />
            Configure Dependency Rules
          </Box>
        </DialogTitle>
        <DialogContent>
          <DependencyRulesModal
            variable={currentRulesVariable}
            allVariables={variables}
            rulesData={dependencyRulesData}
            onSave={(conditions) => {
              if (!currentRulesVariable) {
                setError('No variable selected for rules update');
                return;
              }

              // Update the variable with new conditions and parsed values
              setVariables(prev => prev.map(v => {
                if (v.id === currentRulesVariable.id) {
                  const allValues = new Set();
                  Object.values(conditions).forEach(conditionValues => {
                    parseValuesUtil(conditionValues).forEach(val => allValues.add(val as string));
                  });
                  return { ...v, conditions, parsedValues: Array.from(allValues) };
                }
                return v;
              }));

              // Re-analyze warnings after updating rules
              setTimeout(() => {
                analyzeWarnings();
              }, 0);

              // Close modal after a short delay to prevent focus issues
              setTimeout(() => {
                setDependencyRulesModalOpen(false);
                setSuccess('Dependency rules saved successfully');
              }, 100);
            }}
            onCancel={() => setDependencyRulesModalOpen(false)}
            onUpdateRules={setDependencyRulesData}
          />
        </DialogContent>
      </Dialog>

      {/* Verification Modal */}
      <Dialog open={verificationModalOpen} onClose={() => setVerificationModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon />
            Variable Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          <VerificationModal
            variables={variables}
            composedIndexConfig={composedIndexConfig}
            variablesVerified={variablesVerified}
            verificationDetails={verificationDetails}
            user={user}
            onVerificationChange={(verified) => {
              setVariablesVerified(verified);
              saveVerificationToStorage(verified, verificationDetails);
            }}
            onVerificationDetailsChange={(details) => {
              setVerificationDetails(details);
              saveVerificationToStorage(variablesVerified, details);
            }}
            onClose={() => setVerificationModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

// Utility functions (defined outside components for reuse)
const parseValuesUtil = (input: string): any[] => {
  try {
    if (!input || !input.trim()) return [];

    let cleaned = input.trim();

    // Handle ranges (start:step:end)
    const rangeMatches = cleaned.match(/(\d+(?:\.\d+)?):(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)/g);
    if (rangeMatches) {
      rangeMatches.forEach(rangeMatch => {
        const range = expandRangeUtil(rangeMatch);
        cleaned = cleaned.replace(rangeMatch, range.join(', '));
      });
    }

    const values = cleaned
      .split(/[,;\n]|(?:\s{2,})/)
      .map(v => v.trim())
      .filter(v => v.length > 0)
      .filter((value, index, self) => self.indexOf(value) === index);

    return values;
  } catch (error) {
    console.error('Error parsing values:', error);
    return [];
  }
};

const expandRangeUtil = (rangeStr: string): string[] => {
  try {
    const parts = rangeStr.split(':');
    if (parts.length !== 3) return [rangeStr];

    const start = parseFloat(parts[0]);
    const step = parseFloat(parts[1]);
    const end = parseFloat(parts[2]);

    if (isNaN(start) || isNaN(step) || isNaN(end) || step <= 0) {
      return [rangeStr];
    }

    const values = [];
    for (let i = start; i <= end; i += step) {
      values.push((Math.round(i * 100) / 100).toString());
    }

    return values;
  } catch (error) {
    console.error('Error expanding range:', error);
    return [rangeStr];
  }
};

// Modal Components
const VariableModal: React.FC<{
  variable: Variable | undefined;
  allVariables: Variable[];
  composedIndexConfig: ComposedIndexConfig;
  matrixData: any[];
  onSave: (variable: Variable) => void;
  onCancel: () => void;
  onOpenDependencyRules: (variable: Variable) => void;
  generateComposedIndex: (row: any) => string;
}> = ({ variable, allVariables, composedIndexConfig, matrixData, onSave, onCancel, onOpenDependencyRules, generateComposedIndex }) => {
  const [formData, setFormData] = useState<Variable>(
    variable || {
      id: Date.now().toString(),
      name: '',
      type: 'independent',
      values: '',
      parsedValues: [],
      description: ''
    }
  );

  // Update formData when variable prop changes (e.g., when rules are configured)
  React.useEffect(() => {
    if (variable) {
      setFormData(variable);
    }
  }, [variable]);

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Please enter a variable name');
      return;
    }

    // Validation for fromList type
    if (formData.type === 'fromList') {
      if (!formData.fromListData?.composedIndex) {
        alert('Please select a composed index');
        return;
      }

      const valueListData = formData.fromListData?.valueList || [];
      if (valueListData.length === 0 || valueListData.every(row => !row.composedIndex && !row.value)) {
        alert('Please add at least one value list entry');
        return;
      }
    }

    // Parse values based on type
    let parsedValues: any[] = [];
    if (formData.type === 'independent' && formData.values) {
      parsedValues = parseValuesUtil(formData.values);
    } else if (formData.type === 'fromList' && formData.fromListData?.valueList) {
      // Populate parsedValues with unique values from the value list
      const uniqueValues = Array.from(new Set(
        formData.fromListData.valueList
          .map(item => item.value)
          .filter(val => val && val.trim() !== '')
      ));
      parsedValues = uniqueValues;
    }

    const updatedVariable: Variable = {
      ...formData,
      parsedValues
    };

    onSave(updatedVariable);
  };

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }} sx={{ p: 2 }}>
      <TextField
        fullWidth
        label="Variable Name"
        value={formData.name}
        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Variable Type</InputLabel>
        <Select
          value={formData.type}
          label="Variable Type"
          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Variable['type'] }))}
          MenuProps={getZoomCompensatedSelectMenuProps()}
        >
          <MenuItem value="independent">Independent</MenuItem>
          <MenuItem value="dependent">Dependent</MenuItem>
          <MenuItem value="automatic">Automatic (Formula)</MenuItem>
          <MenuItem value="fromList">From List</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Description"
        value={formData.description || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        sx={{ mb: 2 }}
      />

      {formData.type === 'independent' && (
        <TextField
          fullWidth
          label="Values"
          value={formData.values || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, values: e.target.value }))}
          helperText="Comma-separated values or ranges (e.g., 1,2,3 or 1:0.5:5)"
          sx={{ mb: 2 }}
        />
      )}

      {formData.type === 'dependent' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Parent Variable</InputLabel>
            <Select
              value={formData.parent || ''}
              label="Parent Variable"
              onChange={(e) => setFormData(prev => ({ ...prev, parent: e.target.value }))}
              MenuProps={getZoomCompensatedSelectMenuProps()}
            >
              {allVariables
                .filter(v => v.id !== formData.id)
                .map(v => (
                  <MenuItem key={v.id} value={v.name}>{v.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Dependency Rules
          </Typography>
          {formData.parent && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onOpenDependencyRules(formData)}
                sx={{ mb: 1 }}
              >
                Configure Rules in Table
              </Button>
              <Typography variant="caption" color="text.secondary">
                Click to open table interface for easier copy-paste of multiple rules
              </Typography>
            </Box>
          )}
        </>
      )}

      {formData.type === 'automatic' && (
        <TextField
          fullWidth
          label="Formula"
          value={formData.formula || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, formula: e.target.value }))}
          helperText="Use variable names and mathematical functions (e.g., Hs * 2 + Tp)"
          sx={{ mb: 2 }}
        />
      )}

      {formData.type === 'fromList' && (
        <FromListSection
          formData={formData}
          setFormData={setFormData}
          allVariables={allVariables}
          composedIndexConfig={composedIndexConfig}
          matrixData={matrixData}
          generateComposedIndex={generateComposedIndex}
        />
      )}

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={!formData.name.trim()}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

// FromList Section Component
const FromListSection: React.FC<{
  formData: Variable;
  setFormData: React.Dispatch<React.SetStateAction<Variable>>;
  allVariables: Variable[];
  composedIndexConfig: ComposedIndexConfig;
  matrixData: any[];
  generateComposedIndex: (row: any) => string;
}> = ({ formData, setFormData, allVariables, composedIndexConfig, matrixData, generateComposedIndex }) => {
  const [valueListData, setValueListData] = useState<Array<{ composedIndex: string; value: string }>>(
    formData.fromListData?.valueList || [{ composedIndex: '', value: '' }]
  );

  // Update formData when valueListData changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      fromListData: {
        composedIndex: prev.fromListData?.composedIndex || '',
        valueList: valueListData
      }
    }));
  }, [valueListData, setFormData]);

  // Update valueListData when formData.fromListData changes (for editing existing variables)
  React.useEffect(() => {
    if (formData.fromListData?.valueList && formData.fromListData.valueList.length > 0) {
      setValueListData(formData.fromListData.valueList);
    }
  }, [formData.fromListData?.valueList]);

  // Auto-populate table when matrix data becomes available (only for new/empty variables)
  React.useEffect(() => {
    if (formData.fromListData?.composedIndex === 'composed' && composedIndexConfig.enabled && matrixData.length > 0) {
      // Only auto-populate if the valueList is empty or all values are empty (new variable)
      const hasExistingValues = formData.fromListData.valueList.some(item => item.value && item.value.trim() !== '');
      if (!hasExistingValues) {
        updateValueListFromComposedIndex('composed');
      }
    }
  }, [matrixData.length, formData.fromListData?.composedIndex, composedIndexConfig.enabled, formData.fromListData?.valueList]);

  const handleComposedIndexChange = (composedIndex: string) => {
    setFormData(prev => ({
      ...prev,
      fromListData: {
        composedIndex,
        valueList: prev.fromListData?.valueList || valueListData
      }
    }));

    // Auto-populate table if composed index is selected
    if (composedIndex === 'composed') {
      updateValueListFromComposedIndex(composedIndex);
    }
  };

  const updateValueListFromComposedIndex = (composedIndexValue: string) => {
    if (composedIndexValue === 'composed' && composedIndexConfig.enabled && matrixData.length > 0) {
      // Generate composed index values from actual matrix data
      const composedIndexValues = matrixData.map(row => {
        const composedIndex = generateComposedIndex(row);
        return {
          composedIndex: composedIndex,
          value: '' // Empty value for user to fill
        };
      });

      // Remove duplicates based on composed index
      const uniqueValues = [];
      const seenIndexes = new Set();

      for (const item of composedIndexValues) {
        if (!seenIndexes.has(item.composedIndex) && item.composedIndex) {
          seenIndexes.add(item.composedIndex);
          uniqueValues.push(item);
        }
      }

      // Sort unique values by composed index
      uniqueValues.sort((a, b) => a.composedIndex.localeCompare(b.composedIndex));

      setValueListData(uniqueValues.length > 0 ? uniqueValues : [{ composedIndex: '', value: '' }]);
    } else {
      // Clear table
      setValueListData([{ composedIndex: '', value: '' }]);
    }
  };

  const addValueListRow = () => {
    setValueListData(prev => [...prev, { composedIndex: '', value: '' }]);
  };

  const updateValueListRow = (index: number, field: 'composedIndex' | 'value', value: string) => {
    // For FromList variables, store values as-is without expansion
    setValueListData(prev => prev.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    ));
  };

  const handleValueListPaste = (e: React.ClipboardEvent, index: number, field: 'composedIndex' | 'value') => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const lines = pastedData.split('\n').map(line => line.trim()).filter(line => line);

    if (lines.length > 1) {
      if (field === 'value') {
        // Multi-line paste into value field: distribute values across existing rows
        setValueListData(prev => {
          const newData = [...prev];
          lines.forEach((line, lineIndex) => {
            const targetIndex = index + lineIndex;
            if (targetIndex < newData.length) {
              // Parse the value from the line
              let valueStr = line;
              if (line.includes('\t')) {
                const columns = line.split('\t').map(col => col.replace(/^"|"$/g, '').trim());
                valueStr = columns[1] || columns[0] || '';
              } else if (line.includes(',')) {
                const columns = line.split(',').map(col => col.replace(/^"|"$/g, '').trim());
                valueStr = columns[1] || columns[0] || '';
              }

              newData[targetIndex] = { ...newData[targetIndex], value: valueStr };
            }
          });
          return newData;
        });
      } else {
        // Multi-line paste into composedIndex: create new rows
        const newRows: Array<{ composedIndex: string; value: string }> = [];
        const startIndex = index;

        lines.forEach((line, lineIndex) => {
          // Check if line contains separator (tab or comma)
          let columns: string[];
          if (line.includes('\t')) {
            columns = line.split('\t');
          } else if (line.includes(',')) {
            columns = line.split(',');
          } else {
            // No separator, put entire line in the target field
            columns = [line];
          }

          // Clean up columns (remove quotes and trim whitespace)
          columns = columns.map(col => col.replace(/^"|"$/g, '').trim());

          let composedIndex = '';
          let valueStr = '';

          composedIndex = columns[0] || '';
          valueStr = columns[1] || '';

          // For paste operations, treat each line as a single value (no expansion)
          newRows.push({
            composedIndex: composedIndex || valueListData[startIndex]?.composedIndex || '',
            value: valueStr
          });
        });

        // Replace the rows starting from the target index
        setValueListData(prev => {
          const beforeRows = prev.slice(0, startIndex);
          const afterRows = prev.slice(startIndex + newRows.length);
          return [...beforeRows, ...newRows, ...afterRows];
        });
      }
    } else {
      // Single line paste
      const line = lines[0];
      let columns: string[];
      if (line.includes('\t')) {
        columns = line.split('\t');
      } else if (line.includes(',')) {
        columns = line.split(',');
      } else {
        columns = [line];
      }

      // Clean up columns (remove quotes and trim whitespace)
      columns = columns.map(col => col.replace(/^"|"$/g, '').trim());

      if (field === 'composedIndex') {
        updateValueListRow(index, 'composedIndex', columns[0] || '');
        if (columns[1]) {
          // Check if the value contains ranges/lists and expand if needed
          const expandedValues = parseValuesUtil(columns[1]);
          if (expandedValues.length > 1) {
            // Multiple values - expand into multiple rows
            setValueListData(prev => {
              const currentRow = prev[index];
              const beforeRows = prev.slice(0, index);
              const afterRows = prev.slice(index + 1);

              // Update the current row with the first value
              const updatedCurrentRow = { ...currentRow, composedIndex: columns[0] || '', value: expandedValues[0] };

              // Create additional rows for remaining values
              const additionalRows = expandedValues.slice(1).map(value => ({
                composedIndex: columns[0] || '',
                value: value
              }));

              return [...beforeRows, updatedCurrentRow, ...additionalRows, ...afterRows];
            });
          } else {
            updateValueListRow(index, 'value', columns[1]);
          }
        }
      } else {
        const valueToSet = columns[1] || columns[0] || '';
        updateValueListRow(index, 'value', valueToSet);
      }
    }
  };

  const copyValueListToExcel = () => {
    const data = valueListData.filter(row => row.composedIndex || row.value);
    if (data.length === 0) {
      alert('No data to copy');
      return;
    }

    const csvContent = [
      'Composed Index,Value',
      ...data.map(row => `${row.composedIndex},${row.value}`)
    ].join('\n');

    navigator.clipboard.writeText(csvContent).then(() => {
      alert(`${data.length} rows copied to clipboard as comma-separated values`);
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard');
    });
  };

  const pasteValueListFromExcel = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const pastedData = await navigator.clipboard.readText();
        if (pastedData) {
          parseExcelData(pastedData);
        } else {
          alert('No data found in clipboard');
        }
      } else {
        alert('Clipboard API not available. Please copy data and use Ctrl+V in the table.');
      }
    } catch (error) {
      console.error('Failed to read from clipboard:', error);
      alert('Failed to read from clipboard');
    }
  };

  const parseExcelData = (data: string) => {
    const lines = data.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      alert('No data found in clipboard');
      return;
    }

    // Skip header if it exists
    const startIndex = lines[0].toLowerCase().includes('composed') ? 1 : 0;
    const dataLines = lines.slice(startIndex);

    const newRows: Array<{ composedIndex: string; value: string }> = [];

    dataLines.forEach(line => {
      // Handle both tab and comma separated values
      let columns: string[];
      if (line.includes('\t')) {
        columns = line.split('\t');
      } else if (line.includes(',')) {
        columns = line.split(',');
      } else {
        return; // Skip lines that don't have separators
      }

      // Clean up columns (remove quotes and trim whitespace)
      columns = columns.map(col => col.replace(/^"|"$/g, '').trim());

      if (columns.length >= 2 && (columns[0] || columns[1])) {
        newRows.push({
          composedIndex: columns[0] || '',
          value: columns[1] || ''
        });
      }
    });

    if (newRows.length > 0) {
      setValueListData(newRows);
      alert(`Imported ${newRows.length} rows from Excel`);
    } else {
      alert('No valid data found to import');
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Composed Index Selection
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Composed Index</InputLabel>
        <Select
          value={formData.fromListData?.composedIndex || ''}
          label="Composed Index"
          onChange={(e) => handleComposedIndexChange(e.target.value)}
          MenuProps={getZoomCompensatedSelectMenuProps()}
        >
          <MenuItem value="">Select composed index to use</MenuItem>
          {composedIndexConfig.enabled && (
            <MenuItem value="composed">
              {composedIndexConfig.name} ({composedIndexConfig.selectedVariables.length} variables)
            </MenuItem>
          )}
          {/* Add other composed index options here if needed */}
        </Select>
      </FormControl>

      {!composedIndexConfig.enabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No composed index configured. Enable and configure a composed index first.
        </Typography>
      )}

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Value List Configuration
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Configure the mapping between composed index values and their corresponding variable values.
        </Typography>
      </Alert>

      <Box sx={{ mb: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addValueListRow}
            variant="outlined"
          >
            Add Row
          </Button>
          <Button
            size="small"
            startIcon={<CalculateIcon />}
            onClick={() => updateValueListFromComposedIndex(formData.fromListData?.composedIndex || '')}
            variant="outlined"
            disabled={!composedIndexConfig.enabled || matrixData.length === 0}
          >
            Populate from Matrix
          </Button>
          <Button
            size="small"
            startIcon={<FileUploadIcon />}
            onClick={pasteValueListFromExcel}
            variant="outlined"
          >
            Paste from Excel
          </Button>
          <Button
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={copyValueListToExcel}
            variant="outlined"
          >
            Copy to Excel
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Composed Index</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Variable Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {valueListData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Composed index"
                      value={row.composedIndex}
                      disabled={!!formData.fromListData?.composedIndex}
                      onChange={(e) => updateValueListRow(index, 'composedIndex', e.target.value)}
                      onPaste={(e) => handleValueListPaste(e, index, 'composedIndex')}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Variable value"
                      value={row.value}
                      onChange={(e) => updateValueListRow(index, 'value', e.target.value)}
                      onPaste={(e) => handleValueListPaste(e, index, 'value')}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Tips & Shortcuts
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2">Navigation: Enter/↓ Next row, ↑ Previous row, Tab Next cell</Typography>
            <Typography component="li" variant="body2">Copy & Paste: Ctrl+C to copy, Ctrl+V to paste</Typography>
            <Typography component="li" variant="body2">Multi-line paste from Excel supported</Typography>
            <Typography component="li" variant="body2">Range expansion: Enter "1:1:5" or "1,2,3,4,5" in value field to auto-expand into multiple rows</Typography>
          </Box>
        </Alert>
      </Box>
    </Box>
  );
};

export default LCMLabPage;

const ComposedIndexModal: React.FC<{
  config: ComposedIndexConfig;
  variables: Variable[];
  onSave: (config: ComposedIndexConfig) => void;
  onCancel: () => void;
}> = ({ config, variables, onSave, onCancel }) => {
  const [localConfig, setLocalConfig] = useState<ComposedIndexConfig>(config);

  const handleSave = () => {
    onSave(localConfig);
  };

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }} sx={{ p: 2 }}>
      <TextField
        fullWidth
        label="Composed Index Name"
        value={localConfig.name}
        onChange={(e) => setLocalConfig(prev => ({ ...prev, name: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Separator"
        value={localConfig.separator}
        onChange={(e) => setLocalConfig(prev => ({ ...prev, separator: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Select Variables for Composed Index
      </Typography>

      <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
        {variables.map(variable => (
          <FormControlLabel
            key={variable.id}
            control={
              <Switch
                checked={localConfig.selectedVariables.includes(variable.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setLocalConfig(prev => ({
                      ...prev,
                      selectedVariables: [...prev.selectedVariables, variable.id]
                    }));
                  } else {
                    setLocalConfig(prev => ({
                      ...prev,
                      selectedVariables: prev.selectedVariables.filter(id => id !== variable.id)
                    }));
                  }
                }}
              />
            }
            label={variable.name}
          />
        ))}
      </Box>

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          Save
        </Button>
      </Box>
    </Box>
  );
};

const WarningsModal: React.FC<{ warnings: Warning[] }> = ({ warnings }) => {
  return (
    <Box sx={{ p: 2 }}>
      {warnings.map((warning, index) => (
        <Alert key={index} severity={warning.severity} sx={{ mb: 2 }}>
          <AlertTitle>{warning.title}</AlertTitle>
          <Typography variant="body2">{warning.description}</Typography>
          {warning.variables.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="bold">Affected Variables:</Typography>
              {warning.variables.map((variable, varIndex) => (
                <Typography key={varIndex} variant="body2" sx={{ ml: 2 }}>
                  • {variable.name}: {variable.issue}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      ))}
    </Box>
  );
};

const TemplateSelectionModal: React.FC<{
  templates: Record<string, any>;
  onSelectTemplate: (templateKey: string) => void;
  onCancel: () => void;
}> = ({ templates, onSelectTemplate, onCancel }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Select a template to load predefined variables and configurations.
      </Typography>

      <Grid container spacing={2}>
        {Object.entries(templates).map(([key, template]) => (
          <Grid item xs={12} sm={6} key={key}>
            <Card
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => onSelectTemplate(key)}
            >
              <CardContent>
                <Typography variant="h6">{(template as any).name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(template as any).description}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Variables: {(template as any).variables.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onCancel}>Cancel</Button>
      </Box>
    </Box>
  );
};

const DependencyRulesModal: React.FC<{
  variable: Variable | undefined;
  allVariables: Variable[];
  rulesData: Array<{ parentValue: string; childValues: string }>;
  onSave: (rules: Record<string, string>) => void;
  onCancel: () => void;
  onUpdateRules: (rulesData: Array<{ parentValue: string; childValues: string }>) => void;
}> = ({ variable, allVariables, rulesData, onSave, onCancel, onUpdateRules }) => {
  const handleSave = () => {
    const conditions: Record<string, string> = {};
    if (rulesData) {
      rulesData.forEach(rule => {
        if (rule.childValues.trim()) {
          conditions[rule.parentValue] = rule.childValues.trim();
        }
      });
    }
    onSave(conditions);
  };

  const handleRuleChange = (index: number, field: 'parentValue' | 'childValues', value: string) => {
    const newRulesData = [...rulesData];
    newRulesData[index] = { ...newRulesData[index], [field]: value };
    onUpdateRules(newRulesData);
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const lines = pastedData.split('\n').map(line => line.trim()).filter(line => line);

    if (lines.length > 1) {
      // Multi-line paste: distribute across multiple rows
      const newRulesData = [...rulesData];
      lines.forEach((line, lineIndex) => {
        const targetIndex = index + lineIndex;
        if (targetIndex < newRulesData.length) {
          newRulesData[targetIndex] = { ...newRulesData[targetIndex], childValues: line };
        }
      });
      onUpdateRules(newRulesData);
    } else {
      // Single line paste
      handleRuleChange(index, 'childValues', pastedData.trim());
    }
  };

  if (!variable || !variable.parent) {
    return null;
  }

  const parentVar = allVariables.find(v => v.name === variable.parent);

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Dependency Rules for "{variable.name}"
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define child values for each parent value. You can copy-paste multiple values from Excel or other sources.
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
                {parentVar?.name} (Parent)
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {variable.name} Values (Child)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rulesData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No parent values found. Make sure the parent variable has values defined.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rulesData.map((rule, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 'medium', bgcolor: 'action.hover' }}>
                    {rule.parentValue}
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={rule.childValues}
                      onChange={(e) => handleRuleChange(index, 'childValues', e.target.value)}
                      onPaste={(e) => handlePaste(e, index)}
                      placeholder="Enter child values (comma-separated or ranges like 1:1:5)"
                      helperText="Tip: Copy-paste from Excel for multiple values. Use ranges like 2:1:6 for 2,3,4,5,6"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        💡 Pro tip: Copy multiple rows from Excel and paste them here. Each line will be distributed across the rows.
      </Typography>

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          Save Rules
        </Button>
      </Box>
    </Box>
  );
};

const VerificationModal: React.FC<{
  variables: Variable[];
  composedIndexConfig: ComposedIndexConfig;
  variablesVerified: boolean;
  verificationDetails: { verifiedBy?: string; verifiedAt?: string };
  user: User | null;
  onVerificationChange: (verified: boolean) => void;
  onVerificationDetailsChange: (details: { verifiedBy?: string; verifiedAt?: string }) => void;
  onClose: () => void;
}> = ({ variables, composedIndexConfig, variablesVerified, verificationDetails, user, onVerificationChange, onVerificationDetailsChange, onClose }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Variable Verification
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review all variables and their configurations below. Check the verification box when you have confirmed all variables are set correctly.
      </Typography>

      {/* Variables Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Variables Summary ({variables.length} variables)
        </Typography>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Values/Formula</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Conditions/Rules</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No variables defined
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                variables.map((variable) => (
                  <TableRow key={variable.id}>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {variable.name || 'Unnamed'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={variable.type}
                        size="small"
                        color={
                          variable.type === 'independent' ? 'success' :
                            variable.type === 'dependent' ? 'warning' :
                              variable.type === 'automatic' ? 'info' : 'secondary'
                        }
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {variable.type === 'independent' && variable.values}
                        {variable.type === 'dependent' && `Depends on: ${variable.parent}`}
                        {variable.type === 'automatic' && `Formula: ${variable.formula}`}
                        {variable.type === 'fromList' && `${variable.fromListData?.valueList?.length || 0} mappings`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {variable.type === 'dependent' && variable.conditions && (
                        <Box sx={{ maxHeight: 100, overflow: 'auto' }}>
                          {Object.entries(variable.conditions).map(([parentValue, childValues]) => (
                            <Typography key={parentValue} variant="caption" display="block" sx={{ mb: 0.5 }}>
                              <strong>{parentValue}:</strong> {parseValuesUtil(childValues).join(', ')}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {variable.type !== 'dependent' && (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <Typography variant="body2" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {variable.description || 'No description'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Composed Index Summary */}
      {composedIndexConfig.enabled && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Composed Index Configuration
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2">
              <strong>Name:</strong> {composedIndexConfig.name}
            </Typography>
            <Typography variant="body2">
              <strong>Separator:</strong> "{composedIndexConfig.separator}"
            </Typography>
            <Typography variant="body2">
              <strong>Variables:</strong> {composedIndexConfig.selectedVariables.length} selected
            </Typography>
            {composedIndexConfig.selectedVariables.length > 0 && (
              <Typography variant="body2">
                <strong>Selected:</strong> {variables.filter(v => composedIndexConfig.selectedVariables.includes(v.id)).map(v => v.name).join(', ')}
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* Verification Details */}
      {verificationDetails.verifiedBy && verificationDetails.verifiedAt && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'success.dark' }}>
            ✓ Previously Verified
          </Typography>
          <Typography variant="body2" color="success.dark">
            Verified by: <strong>{verificationDetails.verifiedBy}</strong>
          </Typography>
          <Typography variant="body2" color="success.dark">
            Verified at: <strong>{new Date(verificationDetails.verifiedAt).toLocaleString()}</strong>
          </Typography>
        </Box>
      )}

      {/* Verification Checkbox */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={variablesVerified}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onVerificationChange(e.target.checked);
                if (e.target.checked) {
                  onVerificationDetailsChange({
                    verifiedBy: user?.name,
                    verifiedAt: new Date().toISOString()
                  });
                } else {
                  onVerificationDetailsChange({});
                }
              }}
              color="primary"
            />
          }
          label={
            <Typography variant="body1" fontWeight="medium">
              Variables Verified
            </Typography>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
          I have reviewed all variables and their configurations. All settings appear correct for generating the LCM.
        </Typography>
      </Box>

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Box>
  );
};
